import { unwrap } from "@sagot/api-client";
import { EMAIL_MAX_LENGTH, SSO_ERROR_QUERY } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Field, Icon, Input } from "@sagot/ui";
import { useEffect, useRef, useState, type SyntheticEvent } from "react";
import { useSearchParams } from "react-router";
import { useErrorMessage } from "../lib/api-errors";
import { useApi } from "../session/session-provider";
import { WORK_EMAIL_PLACEHOLDER } from "./auth-constants";
import { FormAlert } from "./components/form-alert";

const REASONS = ["failed", "unavailable", "not_a_member", "domain", "expired"] as const;
type Reason = (typeof REASONS)[number];
const reasonOf = (value: string | null): Reason | null => (value === null ? null : ((REASONS as readonly string[]).includes(value) ? (value as Reason) : "failed"));
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The address typed in the password form next to this entry (mockup: it is copied into the SSO field). The page passes its value as a
 * prop, but the form does not re-render while one types, so the prop is stale until the first failed attempt: read the field itself.
 */
function typedEmail(from: HTMLElement | null, fallback: string): string {
  const field = from?.closest(".a-card")?.querySelector<HTMLInputElement>("form:not(.sso-pane) input[type='email']");
  const typed = field?.value.trim() ?? "";
  return typed === "" ? fallback : typed;
}

interface SsoEntryProps {
  /** What the password form already holds; prefills the work e-mail. */
  email: string;
  /** The password sign-in was refused with `sso_enforced`: open the entry straight away (mount it with a `key` that changes with this). */
  enforced: boolean;
}

/**
 * Sign-in page entry for staff single sign-on. Closed, it is the outlined "Sign in with SSO" button under Microsoft. Open, it is the
 * mockup's SSO view: it takes the whole card (auth.css hides the sign-in view around it) with the serif title, the work e-mail and
 * "Continue with SSO" / "Back to sign in". It asks the API for the workspace's provider of that domain (`POST /v1/auth/sso/discover`)
 * and goes there with a full page redirect. Shows why a previous attempt failed (`?sso_error=`).
 */
export function SsoEntry({ email, enforced }: SsoEntryProps) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const [params] = useSearchParams();
  const failure = reasonOf(params.get(SSO_ERROR_QUERY));
  const [open, setOpen] = useState(enforced || failure !== null);
  const [value, setValue] = useState(email);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const openerRef = useRef<HTMLButtonElement>(null);
  // Set by the visitor's own clicks: the opener only takes focus back after Back to sign in (never on load, also not in StrictMode's
  // second effect run).
  const returned = useRef(false);

  // The view that appears takes focus (mockup `lvGo`): the e-mail field when the pane opens, the opener when the visitor comes back.
  useEffect(() => {
    if (open) emailRef.current?.focus();
    else if (returned.current) openerRef.current?.focus();
  }, [open]);

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setNote(null);
    const address = value.trim();
    if (!EMAIL.test(address) || address.length > EMAIL_MAX_LENGTH) {
      setError(t("auth.common.emailInvalid"));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const found = await unwrap(api.POST("/v1/auth/sso/discover", { body: { email: address } }));
      if (found.sso && found.start_url) {
        window.location.assign(found.start_url);
        return;
      }
      setNote(t("auth.login.ssoNone"));
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Button
        ref={openerRef}
        type="button"
        variant="secondary"
        size="lg"
        className="w-full"
        onClick={(event) => {
          setValue(typedEmail(event.currentTarget, email));
          setError(null);
          setNote(null);
          setOpen(true);
        }}
      >
        <Icon name="key" />
        {t("auth.login.sso")}
      </Button>
    );
  }

  return (
    <form className="sso-pane" noValidate onSubmit={(event) => void submit(event)} aria-labelledby="sso-title" data-testid="sso-entry">
      <h1 id="sso-title" className="a-title">
        {t("auth.login.ssoTitle")}
      </h1>
      <p className="a-sub">{t("auth.login.ssoSub")}</p>
      {failure ? <FormAlert title={t(`auth.login.ssoError.${failure}`)} /> : null}
      {enforced ? <FormAlert title={t("auth.login.ssoEnforced")} /> : null}
      <Field className="a-fld" label={t("auth.common.workEmail")} error={error ?? undefined}>
        <Input
          ref={emailRef}
          type="email"
          autoComplete="email"
          inputMode="email"
          placeholder={WORK_EMAIL_PLACEHOLDER}
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
          }}
        />
      </Field>
      {note ? (
        <p role="status" className="a-note">
          {note}
        </p>
      ) : null}
      <Button type="submit" size="lg" className="w-full" loading={busy}>
        {t("auth.login.ssoContinue")}
      </Button>
      <p className="a-foot">
        <button
          type="button"
          className="a-link"
          onClick={() => {
            returned.current = true;
            setOpen(false);
          }}
        >
          {t("auth.login.ssoBack")}
        </button>
      </p>
    </form>
  );
}
